/**
 * Zugang zur API.
 *
 * Eine Stelle für alles, was jede Anfrage braucht: Cookies mitschicken, den
 * CSRF-Header setzen, das einheitliche Fehlerformat auspacken. Ohne das
 * schreibt jede Komponente ihre eigene Variante, und eine davon vergisst den
 * Header.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: { field?: string; message: string }[] = [],
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Fehler, die an einem bestimmten Feld hängen — für die Formularanzeige. */
  fieldErrors(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const detail of this.details) {
      if (detail.field) map[detail.field] = detail.message
    }
    return map
  }
}

/** Der CSRF-Wert kommt aus `/api/me` und wird hier gehalten. */
let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (UNSAFE.has(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      // Ohne das schickt der Browser das Sitzungs-Cookie nicht mit, sobald
      // Frontend und API in der Entwicklung auf verschiedenen Ports liegen.
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'network_error', 'Keine Verbindung zum Server. Bist du online?')
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const error = payload?.error
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? 'Da ist etwas schiefgegangen.',
      error?.details ?? [],
      error?.request_id,
    )
  }

  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
}

// ------------------------------------------------------------------ Typen ---

export type TenantRef = {
  id: string
  slug: string
  name: string
  role: 'owner' | 'manager' | 'employee'
  /** Grundlage des Farbschemas. Reist mit `/api/me` mit, damit ein
   *  Betriebswechsel die Farbe ohne zweiten Aufruf umstellen kann. */
  primary_color: string
}

export type Me = {
  id: string
  email: string
  full_name: string
  is_superadmin: boolean
  email_verified: boolean
  has_password: boolean
  csrf_token: string
  active_tenant: TenantRef | null
  tenants: TenantRef[]
  impersonated_by: string | null
  impersonation_ends_at: string | null
}

export type Member = {
  membership_id: string
  full_name: string
  role: 'owner' | 'manager' | 'employee'
  is_active: boolean
  employee_no: string | null
  weekly_hours: string | null
  /** Nur für den Inhaber gefüllt. */
  hourly_wage: string | null

  /**
   * Alles ab hier ist leer, solange die Person kein Konto hat. Sie ist
   * trotzdem voll planbar — sie kann sich nur nicht anmelden und bekommt
   * keine Benachrichtigungen.
   */
  user_id: string | null
  email: string | null
  email_verified: boolean
  last_login_at: string | null
  has_account: boolean
  invitation_pending: boolean
}

/** Stammdaten des Betriebs. Alles außer id/slug/name/is_active ist optional. */
export type TenantProfile = {
  id: string
  slug: string
  name: string
  is_active: boolean
  legal_name: string | null
  contact_email: string | null
  phone: string | null
  street: string | null
  postal_code: string | null
  city: string | null
  country: string | null
  vat_id: string | null
}

export type LeaveBalance = {
  membership_id: string
  full_name: string
  year: number
  /** `null` heißt „kein Anspruch hinterlegt" — nicht null Tage. */
  entitled_days: string | null
  carryover_days: string
  taken_days: number
  pending_days: number
  remaining_days: string | null
  remaining_after_pending: string | null
}

export type OpenShift = {
  shift_id: string
  starts_at: string
  ends_at: string
  location_name: string
  position_name: string
  assigned: number
  needed: number
  applied: boolean
}

export type ShiftApplication = {
  application_id: string
  shift_id: string
  membership_id: string
  full_name: string
  starts_at: string
  ends_at: string
  location_name: string
  position_name: string
  status: string
  created_at: string
}

export type DayCost = {
  day: string
  planned_hours: string
  /** `null`, sobald für eine eingeteilte Person kein Lohn hinterlegt ist. */
  planned_cost: string | null
  missing_wages: number
  planned_revenue: string | null
  actual_revenue: string | null
  cost_ratio: string | null
}

export type Invitation = {
  id: string
  email: string
  role: string
  expires_at: string
  created_at: string
}

export type TenantSettings = {
  tenant_id: string
  timezone: string
  locale: string
  week_starts_on: number
  primary_color: string
  holiday_region: string
  retention_planning_days: number
  retention_time_days: number
}

export type AuditEntry = {
  id: string
  created_at: string
  tenant_id: string | null
  actor_user_id: string | null
  impersonator_user_id: string | null
  action: string
  object_type: string | null
  object_id: string | null
  result: string
  meta: Record<string, unknown>
}

export type TenantOverview = {
  tenant_id: string
  slug: string
  name: string
  is_active: boolean
  member_count: number
  created_at: string
}

export type UserOverview = {
  user_id: string
  email: string
  full_name: string
  is_superadmin: boolean
  is_active: boolean
  email_verified: boolean
  locked_until: string | null
  last_login_at: string | null
  tenant_count: number
  created_at: string
}

export type ListResponse<T> = { items: T[]; total: number }

export const ROLE_LABELS: Record<string, string> = {
  owner: 'Inhaber',
  manager: 'Schichtleitung',
  employee: 'Mitarbeiter',
}

// ------------------------------------------------------------- Phase 2 ---

export type Location = {
  id: string
  name: string
  color: string
  sort_order: number
  is_active: boolean
}

export type Position = {
  id: string
  name: string
  abbreviation: string
  color: string
  sort_order: number
  is_active: boolean
}

export type ShiftAssignment = {
  id: string
  membership_id: string
  full_name: string
  override_reason: string | null
}

export type Shift = {
  id: string
  location_id: string
  position_id: string
  starts_at: string
  ends_at: string
  break_minutes: number
  min_staff: number
  max_staff: number
  note: string | null
  is_published: boolean
  assignments: ShiftAssignment[]
}

export type PlanConflict = {
  code: string
  severity: 'legal' | 'warning' | 'info'
  message: string
  detail: Record<string, string | number>
}

export type AssignResult = {
  assignment: ShiftAssignment | null
  conflicts: PlanConflict[]
  needs_reason: boolean
}

export type Absence = {
  id: string
  membership_id: string
  kind: 'vacation' | 'sick' | 'other'
  starts_on: string
  ends_on: string
  status: string
}

export const ABSENCE_LABELS: Record<string, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  other: 'Abwesend',
}

/** Wie dringend ein Konflikt ist — die Farbgebung folgt daraus. */
export const SEVERITY_TONE: Record<PlanConflict['severity'], 'danger' | 'warning' | 'neutral'> = {
  legal: 'danger',
  warning: 'warning',
  info: 'neutral',
}
